import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Star, ArrowLeft, Upload, Trash2, Copy, Check, Gift, Link2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { deleteUser } from 'firebase/auth';
import toast from 'react-hot-toast';
import { auth, db, storage } from '../firebase';
import { buildReferralLink, claimCustomReferralCode, getOrCreateReferralCode } from '../util/referralUtils';
import { claimProfileSlug } from '../util/profileSlugUtils';
import Select from 'react-select';
import { getCountryOptions, getCityOptions, getCountryByName, buildLocationString } from '../data/cities';
import SubscriptionPlanSection from '../components/SubscriptionPlanSection';

import ReactCrop, {
  centerCrop,
  makeAspectCrop,
  type Crop,
  type PixelCrop,
} from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

// Categories matching SearchScreen
const INFLUENCER_TAGS = [
  'Fitness',
  'Tech',
  'Lifestyle',
  'Fashion',
  'Beauty',
  'Gaming',
  'Food',
  'Travel',
  'Education',
  'Sports',
  'Music',
  'Art',
] as const;

// Languages matching SearchScreen
const COMMON_LANGUAGES = [
  'English',
  'Urdu',
  'Spanish',
  'French',
  'German',
  'Italian',
  'Portuguese',
  'Japanese',
  'Korean',
  'Chinese',
  'Arabic',
  'Hindi',
  'Russian'
];

// Helper function to generate cropped image
async function getCroppedImg(
  image: HTMLImageElement,
  crop: PixelCrop,
  fileName: string
): Promise<Blob | null> {
  const canvas = document.createElement('canvas');
  
  // scaleX/Y factor to relate crop pixel coordinates to original image pixel coordinates
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;

  // Dimensions of the crop in original image pixels
  // Use Math.round to avoid sub-pixel issues that might lead to tiny empty lines
  const destWidth = Math.round(crop.width * scaleX);
  const destHeight = Math.round(crop.height * scaleY);

  // Ensure we have valid dimensions for the output canvas
  if (destWidth <= 0 || destHeight <= 0) {
    console.error("Invalid crop dimensions for output canvas:", { destWidth, destHeight, crop, scaleX, scaleY });
    return null;
  }

  canvas.width = destWidth;
  canvas.height = destHeight;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    console.error("Failed to get 2D context from canvas");
    return null;
  }

  ctx.imageSmoothingQuality = 'high'; // Suggests better quality for scaling

  ctx.drawImage(
    image,
    crop.x * scaleX,
    crop.y * scaleY,
    crop.width * scaleX,
    crop.height * scaleY,
    0,
    0,
    destWidth,
    destHeight
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        console.error('Canvas toBlob resulted in null');
        reject(new Error('Canvas toBlob resulted in null'));
        return;
      }
      resolve(blob);
    }, 'image/png', 0.92); // Using PNG for potentially better quality of profile pics, 0.92 quality
  });
}

const SettingsScreen: React.FC = () => {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [bio, setBio] = useState('');
  const [about, setAbout] = useState('');
  const [country, setCountry] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [city, setCity] = useState('');
  const [languages, setLanguages] = useState<string[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [imageUrl, setImageUrl] = useState<any>('');
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [generatingReferral, setGeneratingReferral] = useState(false);
  const [copiedReferral, setCopiedReferral] = useState(false);
  const [showCustomCodeForm, setShowCustomCodeForm] = useState(false);
  const [customCode, setCustomCode] = useState('');
  const [customCodeError, setCustomCodeError] = useState<string | null>(null);
  const [claimingCode, setClaimingCode] = useState(false);
  const [profileSlug, setProfileSlug] = useState<string | null>(null);
  const [slugInput, setSlugInput] = useState('');
  const [slugError, setSlugError] = useState<string | null>(null);
  const [claimingSlug, setClaimingSlug] = useState(false);
  const [copiedSlug, setCopiedSlug] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Cropping state
  const [imageSrcForCrop, setImageSrcForCrop] = useState<string | null>(null);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [croppedImageBlob, setCroppedImageBlob] = useState<Blob | null>(null);
  const [isCropperModalOpen, setIsCropperModalOpen] = useState(false);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) return;
      if (!name && !bio && imageUrl === null) {
        setLoading(true);
      }
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data();
        if (userData) {
          setName(userData.name || '');
          setBio(userData.bio || '');
          setAbout(userData.about || '');
          if (userData.country && userData.city) {
            setCountry(userData.country);
            setCity(userData.city);
            const matched = getCountryByName(userData.country);
            if (matched) setCountryCode(matched.isoCode);
          } else if (userData.location) {
            const parts = userData.location.split(', ');
            if (parts.length >= 2) {
              const cityPart = parts[0];
              const countryPart = parts.slice(1).join(', ');
              setCity(cityPart);
              setCountry(countryPart);
              const matched = getCountryByName(countryPart);
              if (matched) setCountryCode(matched.isoCode);
            }
          }
          setLanguages(userData.languages || []);
          setTags(userData.tags || []);
          setImageUrl(userData.imageUrl || null);
          setReferralCode(userData.referralCode || null);
          setProfileSlug(userData.slug || null);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [user]);

  const handleImageUpload = async (imageFile: File | Blob) => {
    if (!user) return;

    try {
      const storageRef = ref(storage, `profile-images/${user.uid}`);
      await uploadBytes(storageRef, imageFile);
      return await getDownloadURL(storageRef);
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setLoading(true);
    setSuccess(false);

    try {
      const location = city && country ? buildLocationString(city, country) : '';
      const updates: { name: string; bio: string; about: string; location: string; country: string; city: string; languages: string[]; tags: string[]; imageUrl?: string } = {
        name,
        bio,
        about,
        location,
        country,
        city,
        languages,
        tags,
      };

      if (croppedImageBlob) {
        setUploadingImage(true);
        const finalImageFile = new File([croppedImageBlob], `profile_${user.uid}.png`, { type: croppedImageBlob.type || 'image/png' });
        const newFirebaseUrl = await handleImageUpload(finalImageFile);
        setUploadingImage(false);
        updates.imageUrl = newFirebaseUrl;
        setImageUrl(newFirebaseUrl);
        setCroppedImageBlob(null);
      } else if (imageUrl) {
        updates.imageUrl = imageUrl;
      }

      await updateDoc(doc(db, 'users', user.uid), updates);
      setUser({ ...user, ...updates });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Error updating profile:', error);
    } finally {
      setLoading(false);
      setUploadingImage(false);
    }
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setCrop(undefined);
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setImageSrcForCrop(reader.result?.toString() || null);
        setIsCropperModalOpen(true);
      });
      reader.readAsDataURL(e.target.files[0]);
      e.target.value = '';
    }
  };

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    imgRef.current = e.currentTarget;
    const { width, height } = e.currentTarget;
    const initialCrop = centerCrop(
      makeAspectCrop(
        {
          unit: '%',
          width: 90,
        },
        1,
        width,
        height
      ),
      width,
      height
    );
    setCrop(initialCrop);
  };

  const handleCropConfirm = async () => {
    if (completedCrop && imgRef.current && imageSrcForCrop) {
      try {
        if (completedCrop.width > 0 && completedCrop.height > 0) {
          const blob = await getCroppedImg(imgRef.current, completedCrop, 'croppedProfile.png');
          if (blob) {
            setCroppedImageBlob(blob);
            if (imageUrl && imageUrl.startsWith('blob:')) {
              URL.revokeObjectURL(imageUrl);
            }
            setImageUrl(URL.createObjectURL(blob));
          }
        } else {
          console.warn("Crop dimensions are zero, cannot confirm crop.");
        }
      } catch (e) {
        console.error('Crop failed', e);
      } finally {
        setIsCropperModalOpen(false);
      }
    } else {
      console.warn("Crop confirmation called without completed crop or image reference.");
    }
  };
  
  const handleCropCancel = () => {
    setIsCropperModalOpen(false);
  };

  const handleGenerateReferral = async () => {
    if (!user) return;
    setGeneratingReferral(true);
    try {
      const code = await getOrCreateReferralCode(user.uid);
      setReferralCode(code);
      toast.success('Your referral link is ready!');
    } catch (error) {
      console.error('Error generating referral code:', error);
      toast.error('Failed to generate referral link. Please try again.');
    } finally {
      setGeneratingReferral(false);
    }
  };

  const handleClaimCustomCode = async () => {
    if (!user) return;
    setCustomCodeError(null);
    setClaimingCode(true);
    try {
      const result = await claimCustomReferralCode(user.uid, customCode);
      if (result.ok) {
        setReferralCode(result.code);
        setShowCustomCodeForm(false);
        setCustomCode('');
        toast.success('Custom referral code claimed!');
      } else {
        setCustomCodeError(result.reason);
      }
    } catch (error) {
      console.error('Error claiming custom code:', error);
      setCustomCodeError('Something went wrong. Please try again.');
    } finally {
      setClaimingCode(false);
    }
  };

  const handleClaimSlug = async () => {
    if (!user) return;
    setSlugError(null);
    setClaimingSlug(true);
    try {
      const result = await claimProfileSlug(user.uid, slugInput);
      if (result.ok) {
        setProfileSlug(result.slug);
        setSlugInput('');
        toast.success('Profile URL updated!');
      } else {
        setSlugError(result.reason);
      }
    } catch (error) {
      console.error('Error claiming profile slug:', error);
      setSlugError('Something went wrong. Please try again.');
    } finally {
      setClaimingSlug(false);
    }
  };

  const handleCopySlug = async () => {
    if (!profileSlug) return;
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/star/${profileSlug}`);
      setCopiedSlug(true);
      toast.success('Profile link copied!');
      setTimeout(() => setCopiedSlug(false), 2000);
    } catch (error) {
      console.error('Clipboard error:', error);
      toast.error('Could not copy. Please copy manually.');
    }
  };

  const handleCopyReferral = async () => {
    if (!referralCode) return;
    try {
      await navigator.clipboard.writeText(buildReferralLink(referralCode));
      setCopiedReferral(true);
      toast.success('Referral link copied!');
      setTimeout(() => setCopiedReferral(false), 2000);
    } catch (error) {
      console.error('Clipboard error:', error);
      toast.error('Could not copy. Please copy manually.');
    }
  };

  const handleDeleteAccount = async () => {
    if (!user || !auth.currentUser) return;
    setDeletingAccount(true);
    try {
      await deleteDoc(doc(db, 'users', user.uid));
      await deleteUser(auth.currentUser);
      setUser(null);
      toast.success('Your account has been deleted.');
      navigate('/login');
    } catch (error: any) {
      console.error('Error deleting account:', error);
      if (error?.code === 'auth/requires-recent-login') {
        toast.error('For security, please log out and log back in, then try again.');
      } else {
        toast.error('Failed to delete account. Please try again.');
      }
      setShowDeleteModal(false);
    } finally {
      setDeletingAccount(false);
    }
  };


  if (loading && !name && !bio && imageUrl === null && !imageSrcForCrop) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500" />
      </div>
    );
  }

  const isProfileIncomplete = !user?.bio || !user?.name || !user?.imageUrl;

  return (
    <div className="min-h-screen bg-gray-100 py-6 sm:py-12">
      <main className="container mx-auto max-w-3xl px-4">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6 text-center sm:text-left">Settings</h1>

        {isProfileIncomplete && (
          <div className="mb-6 p-4 rounded-lg bg-amber-50 border-2 border-amber-300">
            <p className="font-semibold text-amber-900">Complete your profile to continue</p>
            <p className="text-sm text-amber-700 mt-1">
              Please fill in your <strong>name</strong>, <strong>bio</strong>, and <strong>profile photo</strong> before you can access the rest of the platform.
            </p>
          </div>
        )}

        <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
          <div className="p-6 sm:p-8 space-y-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Profile Image <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-4 sm:gap-6">
                  <div 
                    className="h-32 w-32 rounded-md bg-gray-100 overflow-hidden flex-shrink-0 cursor-pointer"
                    onClick={() => fileInputRef.current?.click()}
                    title="Click to change image"
                  >
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={name || 'Profile'}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gray-100">
                         <Upload className="h-12 w-12 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div>
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept="image/*"
                      className="hidden"
                      onChange={onFileChange}
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                    >
                      <Upload className="h-4 w-4" />
                      Upload Image
                    </button>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-gray-900 focus:ring-purple-500 focus:border-purple-500 sm:text-sm placeholder-gray-400"
                  placeholder="Your Name"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="bio" className="block text-sm font-medium text-gray-700">
                  Bio <span className="text-red-500">*</span> <span className="text-gray-500 text-xs">(Short description, max 50 words)</span>
                </label>
                <textarea
                  id="bio"
                  value={bio}
                  onChange={(e) => {
                    const words = e.target.value.trim().split(/\s+/).filter(Boolean);
                    if (words.length <= 50 || e.target.value.length < bio.length) {
                      setBio(e.target.value);
                    }
                  }}
                  rows={3}
                  className={`w-full border rounded-md px-3 py-2 text-gray-900 focus:ring-purple-500 focus:border-purple-500 sm:text-sm min-h-[80px] placeholder-gray-400 ${
                    bio.trim().split(/\s+/).filter(Boolean).length > 50 ? 'border-red-400' : 'border-gray-300'
                  }`}
                  placeholder="A short bio about yourself (e.g., Fitness enthusiast, Tech lover)"
                />
                <div className={`text-xs text-right ${
                  bio.trim().split(/\s+/).filter(Boolean).length > 45 ? 'text-orange-500' : 'text-gray-400'
                }`}>
                  {bio.trim() ? bio.trim().split(/\s+/).filter(Boolean).length : 0}/50 words
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="about" className="block text-sm font-medium text-gray-700">
                  About <span className="text-gray-500 text-xs">(Detailed information)</span>
                </label>
                <textarea
                  id="about"
                  value={about}
                  onChange={(e) => setAbout(e.target.value)}
                  rows={6}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-gray-900 focus:ring-purple-500 focus:border-purple-500 sm:text-sm min-h-[150px] placeholder-gray-400"
                  placeholder="Write a detailed description about yourself, your expertise, achievements, and what makes you unique..."
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="tags" className="block text-sm font-medium text-gray-700">
                  Categories/Tags <span className="text-gray-500 text-xs">(Select multiple)</span>
                </label>
                <Select
                  isMulti
                  name="tags"
                  options={INFLUENCER_TAGS.map(tag => ({ value: tag, label: tag }))}
                  value={tags.map(tag => ({ value: tag, label: tag }))}
                  onChange={(selected) => setTags(selected ? selected.map(item => item.value) : [])}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  placeholder="Select your categories..."
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: '42px',
                      borderColor: '#d1d5db',
                      '&:hover': { borderColor: '#9ca3af' },
                    }),
                  }}
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Country
                </label>
                <Select
                  name="country"
                  options={getCountryOptions()}
                  value={country ? { value: country, label: country, isoCode: countryCode } : null}
                  onChange={(selected) => {
                    setCountry(selected ? selected.value : '');
                    setCountryCode(selected ? selected.isoCode : '');
                    setCity('');
                  }}
                  className="basic-single"
                  classNamePrefix="select"
                  isClearable
                  placeholder="Select country..."
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: '42px',
                      borderColor: '#d1d5db',
                      '&:hover': { borderColor: '#9ca3af' },
                    }),
                  }}
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  City
                </label>
                <Select
                  name="city"
                  options={countryCode ? getCityOptions(countryCode) : []}
                  value={city ? { value: city, label: city } : null}
                  onChange={(selected) => setCity(selected ? selected.value : '')}
                  className="basic-single"
                  classNamePrefix="select"
                  isClearable
                  isDisabled={!countryCode}
                  placeholder={countryCode ? "Select city..." : "Select a country first..."}
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: '42px',
                      borderColor: '#d1d5db',
                      '&:hover': { borderColor: '#9ca3af' },
                    }),
                  }}
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="languages" className="block text-sm font-medium text-gray-700">
                  Languages <span className="text-gray-500 text-xs">(Select multiple)</span>
                </label>
                <Select
                  isMulti
                  name="languages"
                  options={COMMON_LANGUAGES.map(lang => ({ value: lang, label: lang }))}
                  value={languages.map(lang => ({ value: lang, label: lang }))}
                  onChange={(selected) => setLanguages(selected ? selected.map(item => item.value) : [])}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  placeholder="Select languages you speak..."
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: '42px',
                      borderColor: '#d1d5db',
                      '&:hover': { borderColor: '#9ca3af' },
                    }),
                  }}
                />
              </div>

              {success && (
                <div className="mt-4 p-3 rounded-md bg-green-50 border border-green-200 text-green-700 text-sm">
                  Profile updated successfully!
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center items-center gap-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold py-2.5 px-4 rounded-md text-sm shadow-sm transition-colors disabled:opacity-70 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500"
              >
                {loading && !success ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                    {uploadingImage ? 'Uploading image...' : 'Saving...'}
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </form>
          </div>
        </div>

        <div className="mt-8 bg-white border border-gray-200 rounded-lg shadow-sm">
          <div className="p-6 sm:p-8">
            <div className="flex items-center gap-2 mb-1">
              <Gift className="h-5 w-5 text-purple-600" />
              <h2 className="text-lg font-semibold text-gray-900">Refer a Friend</h2>
            </div>
            <p className="text-sm text-gray-600">
              Share your link. When someone signs up through it, we'll credit you as their referrer.
            </p>

            {referralCode ? (
              <div className="mt-4 space-y-2">
                <div className="flex items-stretch gap-2">
                  <input
                    type="text"
                    readOnly
                    value={buildReferralLink(referralCode)}
                    className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700 bg-gray-50 truncate focus:outline-none"
                    onFocus={(e) => e.currentTarget.select()}
                  />
                  <button
                    type="button"
                    onClick={handleCopyReferral}
                    className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    {copiedReferral ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                    {copiedReferral ? 'Copied' : 'Copy'}
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-500">
                    Your code: <span className="font-mono font-semibold text-gray-700">{referralCode}</span>
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCustomCodeForm((s) => !s);
                      setCustomCodeError(null);
                      setCustomCode('');
                    }}
                    className="text-xs font-medium text-purple-600 hover:text-purple-700"
                  >
                    {showCustomCodeForm ? 'Cancel' : 'Customize'}
                  </button>
                </div>

                {showCustomCodeForm && (
                  <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-md">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Pick a custom code
                    </label>
                    <div className="flex items-stretch gap-2">
                      <input
                        type="text"
                        value={customCode}
                        onChange={(e) => {
                          setCustomCode(e.target.value.toUpperCase());
                          setCustomCodeError(null);
                        }}
                        placeholder="e.g. FANCODE123"
                        maxLength={20}
                        className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm font-mono uppercase focus:ring-purple-500 focus:border-purple-500"
                      />
                      <button
                        type="button"
                        onClick={handleClaimCustomCode}
                        disabled={claimingCode || !customCode.trim()}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-md text-sm font-semibold hover:bg-purple-700 disabled:opacity-50"
                      >
                        {claimingCode ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                            Claiming...
                          </>
                        ) : (
                          'Claim'
                        )}
                      </button>
                    </div>
                    {customCodeError ? (
                      <p className="text-xs text-red-600 mt-2">{customCodeError}</p>
                    ) : (
                      <p className="text-xs text-gray-500 mt-2">
                        3–20 letters or numbers. Old links keep working.
                      </p>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={handleGenerateReferral}
                disabled={generatingReferral}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded-md text-sm font-semibold disabled:opacity-70"
              >
                {generatingReferral ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                    Generating...
                  </>
                ) : (
                  <>
                    <Gift className="h-4 w-4" />
                    Create my referral link
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {user?.isStarAccount && (
          <div className="mt-8 bg-white border border-gray-200 rounded-lg shadow-sm">
            <div className="p-6 sm:p-8">
              <div className="flex items-center gap-2 mb-1">
                <Link2 className="h-5 w-5 text-purple-600" />
                <h2 className="text-lg font-semibold text-gray-900">Profile URL</h2>
              </div>
              <p className="text-sm text-gray-600">
                Pick a custom URL for your public profile. Once claimed, share it anywhere.
              </p>

              <div className="mt-4 space-y-3">
                {profileSlug && (
                  <div className="flex items-stretch gap-2">
                    <input
                      type="text"
                      readOnly
                      value={`${window.location.origin}/star/${profileSlug}`}
                      className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700 bg-gray-50 truncate focus:outline-none"
                      onFocus={(e) => e.currentTarget.select()}
                    />
                    <button
                      type="button"
                      onClick={handleCopySlug}
                      className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      {copiedSlug ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                      {copiedSlug ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    {profileSlug ? 'Change your URL' : 'Choose your URL'}
                  </label>
                  <div className="flex items-stretch gap-2">
                    <div className="flex-1 flex items-stretch border border-gray-300 rounded-md overflow-hidden focus-within:ring-2 focus-within:ring-purple-500 focus-within:border-purple-500">
                      <span className="flex items-center px-3 bg-gray-50 text-sm text-gray-500 border-r border-gray-300">
                        /star/
                      </span>
                      <input
                        type="text"
                        value={slugInput}
                        onChange={(e) => {
                          setSlugInput(e.target.value.toLowerCase().replace(/\s+/g, '-'));
                          setSlugError(null);
                        }}
                        placeholder={profileSlug || 'your-name'}
                        maxLength={30}
                        className="flex-1 px-3 py-2 text-sm focus:outline-none"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleClaimSlug}
                      disabled={claimingSlug || !slugInput.trim()}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-md text-sm font-semibold hover:bg-purple-700 disabled:opacity-50"
                    >
                      {claimingSlug ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                          Claiming...
                        </>
                      ) : (
                        'Claim'
                      )}
                    </button>
                  </div>
                  {slugError ? (
                    <p className="text-xs text-red-600 mt-2">{slugError}</p>
                  ) : (
                    <p className="text-xs text-gray-500 mt-2">
                      3–30 lowercase letters, numbers, or hyphens. Old links keep working.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {user?.isStarAccount && <SubscriptionPlanSection />}

        <div className="mt-8 bg-white border border-red-200 rounded-lg shadow-sm">
          <div className="p-6 sm:p-8">
            <h2 className="text-lg font-semibold text-gray-900">Danger Zone</h2>
            <p className="text-sm text-gray-600 mt-1">
              Permanently delete your account and all of its data. This action cannot be undone.
            </p>
            <button
              type="button"
              onClick={() => setShowDeleteModal(true)}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 border border-red-300 rounded-md text-sm font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
            >
              <Trash2 className="h-4 w-4" />
              Delete Account
            </button>
          </div>
        </div>
      </main>

      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
            <h3 className="text-xl font-semibold mb-2 text-gray-900">Delete Account?</h3>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete this account? This will permanently remove your profile and you will not be able to recover it.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowDeleteModal(false)}
                disabled={deletingAccount}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteAccount}
                disabled={deletingAccount}
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 disabled:opacity-50"
              >
                {deletingAccount ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                    Deleting...
                  </>
                ) : (
                  'Yes, Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {isCropperModalOpen && imageSrcForCrop && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-lg w-full">
            <h3 className="text-xl font-semibold mb-4 text-gray-800">Crop Your Image</h3>
            <div className="max-h-[60vh] overflow-auto mb-4 flex justify-center">
              <ReactCrop
                crop={crop}
                onChange={(_, percentCrop) => setCrop(percentCrop)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={1}
                minWidth={100}
                minHeight={100}
              >
                <img
                  alt="Crop preview"
                  src={imageSrcForCrop}
                  onLoad={onImageLoad}
                  ref={imgRef}
                  style={{ maxHeight: '50vh', objectFit: 'contain' }}
                />
              </ReactCrop>
            </div>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleCropCancel}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCropConfirm}
                disabled={!completedCrop || !imgRef.current || completedCrop.width === 0 || completedCrop.height === 0}
                className="px-4 py-2 bg-purple-600 text-white rounded-md text-sm font-medium hover:bg-purple-700 disabled:opacity-50"
              >
                Crop & Use Image
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsScreen; 